import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { VentureForm, useVentureActions, useVenturePublish } from "@project-mgmt/feature-ventures";
import { Container, Page } from "@project-mgmt/ui";
import { routes } from "../utils/routes";
import { notify } from "../providers/ToastProvider";
import type { WorkspaceRouteParams } from "../types/routes";
import type { CreateVenture } from "@project-mgmt/validation";

export function CreateVenturePage() {
  const navigate = useNavigate();
  const { workspaceId } = useParams() as WorkspaceRouteParams;

  // Track the created venture ID for publishing
  const [createdVentureId, setCreatedVentureId] = useState<string | null>(null);

  // Venture actions
  const { createVenture } = useVentureActions(workspaceId, notify);

  // Publish hook (only initialized when we have a venture ID)
  const { publishVenture } = useVenturePublish(createdVentureId || "", workspaceId, notify);

  // Handle draft save (called when moving from pitch to media step or clicking Save Draft)
  const handleSaveDraft = async (data: CreateVenture | any) => {
    const result = await createVenture(data as CreateVenture);
    if (result.success && result.data) {
      setCreatedVentureId(result.data.id);
      notify.success("Venture saved as draft");
      return result.data;
    }
    throw new Error("Failed to save draft");
  };

  // Handle publish (changes status to 'live')
  const handlePublish = async (ventureId: string) => {
    // If we don't have the venture ID yet, it means we need to create it first
    // This should not happen as the form handles this, but just in case
    if (!ventureId) {
      throw new Error("No venture to publish");
    }

    const result = await publishVenture();
    if (result.success) {
      navigate(routes.ventureDetail(workspaceId, ventureId));
    }
  };

  // Handle successful venture creation/update (for edit mode - saves changes)
  const handleSubmit = async (data: CreateVenture | any) => {
    const result = await createVenture(data as CreateVenture);
    if (result.success && result.data) {
      navigate(routes.ventureDetail(workspaceId, result.data.id));
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(routes.ventures(workspaceId));
  };

  return (
    <Container>
      <Page>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-textPrimary text-2xl font-bold">Create New Venture</h1>
          <p className="text-textSecondary mt-1 text-sm">
            Create a venture proposal to seek financial backing for your business idea
          </p>
        </div>

        {/* Venture Form */}
        <VentureForm
          workspaceId={workspaceId}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onCancel={handleCancel}
        />
      </Page>
    </Container>
  );
}
